import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'reactstrap';
import images from 'assets/images';
import { useLocation } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useBoolean } from 'usehooks-ts';

import Button from 'components/App/Button';
import CardPicture from 'pages/Location/LocationDetail/ManagerPicture/CardPicture';
import Loading from 'components/App/Loading';
import ModalRequestAction from 'components/Modals/ModalRequestAction';

import { ReactComponent as AddPictureIcon } from 'assets/images/add-picture.svg';
import { LIMIT_FILE_SIZE, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import {
  normalizeUploadedMediaUrls,
  splitUploadMediaFiles,
} from 'utils/formatUploadImages';
import buildRoomMediaList from 'utils/buildRoomMediaList';
import moveItemInArray from 'utils/moveItemInArray';
import { showToastError } from 'utils';

import styles from './SectionRoomPictureList.module.scss';

const OPTION_UPLOAD_IMAGES = {
  limitValue: 40,
  fileSize: LIMIT_FILE_SIZE.LARGE,
  videoFileSize: LIMIT_FILE_SIZE.BIG,
};

function SectionRoomPictureList(props) {
  const {
    isLoadingSetRoomPreviewImage,
    isLoadingAddImagesToRooms,
    isChangePositionRoomImages,
    isLoadingDeleteRoomImages,
    isLoadingUpdateRoomVideo,
    isLoadingDeleteRoomVideo,
    isLoadingUploadMultipleMedia,
    isLoadingUploadVideo,

    roomId,
    title,
    roomNames,
    roomType,

    roomPictureList,
    roomVideo,
    roomPreviewImage,

    actionSetRoomPreviewImage,
    actionDeleteRoomImages,
    actionDeleteRoomVideo,
    actionAddImagesToRooms,
    actionUpdateRoomVideo,
    actionChangePositionRoomImages,
    actionGetRoomNamesSetting,
    actionUploadMultiMedia,
    actionUploadVideo,
    onUpdateRoomImageList,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const inputImageFileRef = React.useRef(null);
  const inputVideoFileRef = React.useRef(null);

  const [pictureList, setPictureList] = React.useState(roomPictureList);
  const [pictureSelected, setPictureSelected] = React.useState([]);
  const {
    value: isOpenModalDeleteImages,
    setFalse: onCloseModalDeleteImages,
    setTrue: onOpenModalDeleteImages,
    toggle: onToggleModalDeleteImages,
  } = useBoolean(false);

  // DELETE IMAGES
  const onCancelModalDeleteImages = React.useCallback(() => {
    onCloseModalDeleteImages();
    setPictureSelected([]);
  }, [onCloseModalDeleteImages]);

  const onConfirmDeleteSelectedImages = React.useCallback(() => {
    if (!pictureSelected.length) {
      showToastError('Phòng không được để trống');
      return;
    }

    const selectedImages = pictureSelected.filter((item) => !item.isVideo);
    const hasSelectedVideos = pictureSelected.some((item) => item.isVideo);

    const callbackDeleteSuccess = () => {
      const newImages = pictureList.filter((item) => (
        !pictureSelected.includes(item)
      ));

      onCloseModalDeleteImages();
      setPictureList(newImages);
      setPictureSelected([]);
    };

    if (selectedImages.length) {
      actionDeleteRoomImages({
        placeId,
        roomIds: [roomId],
        images: selectedImages.map((item) => item.image || item),
        callback: !hasSelectedVideos ? callbackDeleteSuccess : undefined,
      });
    }

    if (hasSelectedVideos) {
      actionDeleteRoomVideo({
        placeId,
        roomId,
        callback: callbackDeleteSuccess,
      });
    }
  }, [
    actionDeleteRoomImages,
    actionDeleteRoomVideo,
    onCloseModalDeleteImages,
    pictureList,
    pictureSelected,
    placeId,
    roomId,
  ]);

  // Drop And Drag
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Select Picture
  const onSelectPicture = React.useCallback((picture, checked) => {
    if (checked) {
      setPictureSelected((prevState) => [...prevState, picture]);
    } else {
      setPictureSelected((prevState) => prevState.filter((item) => item.id !== picture.id));
    }
  }, []);

  // Select All Picture
  const onSelectAll = React.useCallback(() => {
    setPictureSelected(pictureList);
  }, [pictureList]);

  // Unselect All Picture
  const onUnSelectAll = React.useCallback(() => {
    setPictureSelected([]);
  }, []);

  const onDragEnd = React.useCallback((e) => {
    const { active, over } = e;
    if (active.id !== over.id) {
      const cloneArray = [...roomPictureList];
      const oldIndex = cloneArray.findIndex((pic) => pic.id === active.id);
      const newIndex = cloneArray.findIndex((pic) => pic.id === over.id);

      arrayMove(cloneArray, oldIndex, newIndex);
      moveItemInArray(cloneArray, oldIndex, newIndex);

      onUpdateRoomImageList({ roomId, images: cloneArray });
      const newSortedArray = cloneArray.map((item) => item.image);
      actionChangePositionRoomImages({ roomId, placeId, images: newSortedArray });
    }
  }, [roomPictureList, roomId, placeId]);

  const onClickOpenImageFileInput = React.useCallback(() => {
    if (inputImageFileRef.current) {
      inputImageFileRef.current.click();
    }
  }, []);

  const onClickOpenVideoFileInput = React.useCallback(() => {
    if (inputVideoFileRef.current) {
      inputVideoFileRef.current.click();
    }
  }, []);

  const onChangeAddImages = React.useCallback(async (e) => {
    const { files } = e.target;

    if (!files?.length) {
      return;
    }

    const { images: imageFiles } = splitUploadMediaFiles(
      files,
      OPTION_UPLOAD_IMAGES,
    );
    inputImageFileRef.current.value = '';

    if (!imageFiles.length) {
      return;
    }

    actionUploadMultiMedia({
      images: imageFiles,
      callback: (uploadedImages) => {
        const normalized = normalizeUploadedMediaUrls(uploadedImages);
        if (normalized.length) {
          actionAddImagesToRooms({
            placeId,
            roomId,
            images: normalized,
          });
        }
      },
    });
  }, [
    actionAddImagesToRooms,
    actionUploadMultiMedia,
    placeId,
    roomId,
  ]);

  const onChangeAddVideo = React.useCallback(async (e) => {
    const { files } = e.target;

    if (!files?.length) {
      return;
    }

    const { videos: videoFiles } = splitUploadMediaFiles(
      files,
      OPTION_UPLOAD_IMAGES,
    );
    inputVideoFileRef.current.value = '';

    if (!videoFiles.length) {
      return;
    }

    const uploadedVideos = await Promise.all(
      videoFiles.map((item) => new Promise((resolve) => {
        actionUploadVideo({
          file: item.file,
          callback: (uploadedVideo) => {
            resolve(normalizeUploadedMediaUrls(uploadedVideo));
          },
        });
      })),
    ).then((results) => results.flat());

    if (uploadedVideos.length) {
      actionUpdateRoomVideo({
        placeId,
        roomId,
        video: uploadedVideos.length === 1 ? uploadedVideos[0] : uploadedVideos,
      });
    }
  }, [
    actionUpdateRoomVideo,
    actionUploadVideo,
    placeId,
    roomId,
  ]);

  const getTitle = () => {
    if (title === '') {
      const roomNameLabel = roomNames.find?.((room) => room.value === roomType).label;

      return roomNameLabel;
    }

    return title;
  };

  const onSetRoomPreviewImage = React.useCallback((image, thisRoomId) => {
    actionSetRoomPreviewImage({
      placeId,
      thisRoomId,
      image,
    });
  }, [actionSetRoomPreviewImage, placeId]);

  React.useEffect(() => {
    setPictureList(buildRoomMediaList({ images: roomPictureList, video: roomVideo }));
  }, [roomPictureList, roomVideo]);

  React.useEffect(() => {
    actionGetRoomNamesSetting();
  }, [actionGetRoomNamesSetting]);

  return (
    <>
      <Loading
        loading={
          isLoadingSetRoomPreviewImage
          || isLoadingAddImagesToRooms
          || isChangePositionRoomImages
          || isLoadingDeleteRoomImages
          || isLoadingUpdateRoomVideo
          || isLoadingDeleteRoomVideo
          || isLoadingUploadMultipleMedia
          || isLoadingUploadVideo
        }
        loaderOnPage
      />
      <Card className={styles.Content}>
        <div className="Section">
          {(() => {
            const imageList = pictureList.filter((pic) => !pic.isVideo);
            const videoList = pictureList.filter((pic) => pic.isVideo);
            return (
              <>
                <h2 className="Section__Title">{`${getTitle()} - Hình ảnh (${imageList.length})`}</h2>

                <div className="Section__Button_Wrapper">
                  <div className="d-flex">
                    <Button
                      classes="Section__Button_SelectAll"
                      onClick={onSelectAll}
                      color={themeColor.LINK}
                    >
                      Chọn tất cả
                    </Button>
                    <Button
                      classes="Section__Button_Add"
                      disabled={!pictureSelected.length}
                      onClick={onUnSelectAll}
                      color={themeColor.LINK}
                    >
                      Bỏ chọn tất cả
                    </Button>
                    <Button
                      classes="Section__Button_Delete"
                      disabled={!pictureSelected.length}
                      color={themeColor.LINK}
                      onClick={onOpenModalDeleteImages}
                    >
                      Xóa
                    </Button>
                  </div>
                </div>

                <div className="Section__PictureList">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={imageList.map((picture) => picture.id)}
                    >
                      {imageList.map((picture) => (
                        <CardPicture
                          picture={picture}
                          roomId={roomId}
                          key={picture.id}
                          onCheckboxChange={onSelectPicture}
                          pictureSelected={pictureSelected}
                          onSetRoomPreviewImage={onSetRoomPreviewImage}
                          roomPreviewImage={roomPreviewImage}
                        />
                      ))}

                      <button
                        type="button"
                        onClick={onClickOpenImageFileInput}
                        className="Section__Upload"
                      >
                        <AddPictureIcon />
                        <span>THÊM ẢNH</span>
                      </button>

                      <input
                        ref={inputImageFileRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        multiple
                        className="Section__Button_AddPicture__Input"
                        onChange={onChangeAddImages}
                      />
                    </SortableContext>
                  </DndContext>
                </div>

                <hr style={{ margin: '32px 0', borderColor: '#EFF2F7' }} />

                <h2 className="Section__Title">{`${getTitle()} - Video (${videoList.length})`}</h2>

                <div className="Section__PictureList">
                  {videoList.map((picture) => (
                    <CardPicture
                      picture={picture}
                      roomId={roomId}
                      key={picture.id}
                      onCheckboxChange={onSelectPicture}
                      pictureSelected={pictureSelected}
                      onSetRoomPreviewImage={onSetRoomPreviewImage}
                      roomPreviewImage={roomPreviewImage}
                    />
                  ))}

                  <button
                    type="button"
                    onClick={onClickOpenVideoFileInput}
                    className="Section__Upload"
                  >
                    <AddPictureIcon />
                    <span>{videoList.length > 0 ? 'SỬA VIDEO' : 'THÊM VIDEO'}</span>
                  </button>

                  <input
                    ref={inputVideoFileRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/*"
                    className="Section__Button_AddPicture__Input"
                    onChange={onChangeAddVideo}
                  />
                </div>
              </>
            );
          })()}
        </div>

        <ModalRequestAction
          isOpen={isOpenModalDeleteImages}
          icon={images.modalGarbageCan.default}
          textRequest={'Bạn có chắc chắn muốn xoá \n hình ảnh này không?'}
          onToggle={onToggleModalDeleteImages}
          onCloseModal={onCancelModalDeleteImages}
          onConfirm={onConfirmDeleteSelectedImages}
        />
      </Card>
    </>
  );
}

SectionRoomPictureList.propTypes = {
  isLoadingSetRoomPreviewImage: PropTypes.bool,
  isLoadingAddImagesToRooms: PropTypes.bool,
  isChangePositionRoomImages: PropTypes.bool,
  isLoadingDeleteRoomImages: PropTypes.bool,
  isLoadingUpdateRoomVideo: PropTypes.bool,
  isLoadingDeleteRoomVideo: PropTypes.bool,
  isLoadingUploadMultipleMedia: PropTypes.bool,
  isLoadingUploadVideo: PropTypes.bool,

  title: PropTypes.string,
  roomId: PropTypes.string,
  roomNames: PropTypes.instanceOf(Object),
  roomType: PropTypes.string,

  roomPictureList: PropTypes.instanceOf(Array),
  roomVideo: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  roomPreviewImage: PropTypes.instanceOf(Object),

  actionSetRoomPreviewImage: PropTypes.func,
  actionAddImagesToRooms: PropTypes.func,
  actionUpdateRoomVideo: PropTypes.func,
  actionDeleteRoomVideo: PropTypes.func,
  actionDeleteRoomImages: PropTypes.func,
  actionChangePositionRoomImages: PropTypes.func,
  actionGetRoomNamesSetting: PropTypes.func,
  actionUploadMultiMedia: PropTypes.func,
  actionUploadVideo: PropTypes.func,
  onUpdateRoomImageList: PropTypes.func,
};

SectionRoomPictureList.defaultProps = {
  isLoadingSetRoomPreviewImage: false,
  isLoadingAddImagesToRooms: false,
  isChangePositionRoomImages: false,
  isLoadingDeleteRoomImages: false,
  isLoadingUpdateRoomVideo: false,
  isLoadingDeleteRoomVideo: false,
  isLoadingUploadMultipleMedia: false,
  isLoadingUploadVideo: false,

  title: '',
  roomId: '',
  roomNames: INITIAL_VALUE.OBJECT,
  roomType: '',

  roomPictureList: INITIAL_VALUE.ARRAY,
  roomVideo: '',
  roomPreviewImage: INITIAL_VALUE.OBJECT,

  actionSetRoomPreviewImage: INITIAL_VALUE.FUNCTION,
  actionAddImagesToRooms: INITIAL_VALUE.FUNCTION,
  actionUpdateRoomVideo: INITIAL_VALUE.FUNCTION,
  actionDeleteRoomVideo: INITIAL_VALUE.FUNCTION,
  actionDeleteRoomImages: INITIAL_VALUE.FUNCTION,
  actionChangePositionRoomImages: INITIAL_VALUE.FUNCTION,
  actionGetRoomNamesSetting: INITIAL_VALUE.FUNCTION,
  actionUploadMultiMedia: INITIAL_VALUE.FUNCTION,
  actionUploadVideo: INITIAL_VALUE.FUNCTION,
  onUpdateRoomImageList: INITIAL_VALUE.FUNCTION,
};

export default React.memo(SectionRoomPictureList);
